import React, { useContext, useEffect, useCallback, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';

import { FEATURE } from '~/common/enums/feature.enum';
import { UserContext } from '~/components/providers/UserProvider';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import WelcomeModalHeader from '~/domains/common/components/WelcomeModal/WelcomeModalHeader';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { isBrowserFn } from '~/utils/browser';

import { Routes } from '../constants/routes';

// eslint-disable-next-line no-restricted-syntax
export enum WelcomeModalType {
  FIRST_LOGIN = 'firstLogin',
  MY_CONTESTS = 'myContests',
}

export function useWelcomeModal(
  type: WelcomeModalType,
  isEnabled: boolean,
  disableBackdropClose = true
) {
  const { user, userId } = useContext(UserContext);
  const { openModal, closeModal, disableClose, enableClose } = useContext(RootModalsContext);
  const { t } = useTranslation('common');
  const router = useRouter();
  const isWelcomeModalsEnabled = useIsFeatureEnabled(FEATURE.ENABLE_WELCOME_MODALS);

  const userBrand = useMemo(() => {
    if (user?.rypUserId && user?.ofpUserId) return 'combo';
    if (user?.rypUserId) return 'ryp';
    if (user?.ofpUserId) return 'ofp';
    return null;
  }, [user]);

  const cookieKey = useMemo(() => {
    if (!isBrowserFn() || !userId) return null;
    return `welcome_modal_shown_${type}_${userId}`;
  }, [userId, type]);
  // This cannot be memoized because it needs to be re-evaluated on every render
  const hasShownWelcomeModal = Cookies.get(cookieKey) || localStorage.getItem(cookieKey);

  const shouldShowModal = useMemo(
    () => isEnabled && isWelcomeModalsEnabled && userBrand && !hasShownWelcomeModal,
    [isEnabled, isWelcomeModalsEnabled, userBrand, hasShownWelcomeModal]
  );

  const markWelcomeModalAsShown = useCallback(() => {
    if (cookieKey) {
      // Storing both cookie and local storage to avoid issues with cookies being blocked by some browsers
      Cookies.set(cookieKey, 'true', { expires: 365 });
      localStorage.setItem(cookieKey, 'true');
    }
  }, [cookieKey]);

  // Dynamic config
  const welcomeModalConfig = useMemo(
    () =>
      type === WelcomeModalType.MY_CONTESTS
        ? {
            type: 'WelcomeModal' as const,
            title: t(`welcomeModal.myContests.title.${userBrand}`),
            headerContent: React.createElement(WelcomeModalHeader, { type: userBrand }),
            description: t('welcomeModal.myContests.description'),
            actionButtonText: t('welcomeModal.myContests.actionButtonText'),
            onActionClick: () => {
              if (disableBackdropClose) {
                enableClose();
              }
              closeModal();
            },
          }
        : {
            type: 'WelcomeModal' as const,
            title: t('welcomeModal.firstLogin.title'),
            description: t('welcomeModal.firstLogin.description'),
            actionButtonText: t('welcomeModal.firstLogin.actionButtonText'),
            headerContent: React.createElement(WelcomeModalHeader, { type: userBrand }),
            onActionClick: () => {
              if (disableBackdropClose) {
                enableClose();
              }
              closeModal();
              router.push(Routes.entries());
            },
          },
    [t, type, userBrand, closeModal, router, disableBackdropClose, enableClose]
  );

  // Initialization
  useEffect(() => {
    if (shouldShowModal) {
      markWelcomeModalAsShown();
      if (disableBackdropClose) {
        disableClose();
      }
      openModal(welcomeModalConfig);
    }
  }, [
    shouldShowModal,
    welcomeModalConfig,
    openModal,
    markWelcomeModalAsShown,
    disableBackdropClose,
    disableClose,
  ]);
}
